import React, { useEffect, useState } from 'react';
import { MyPortfolioTableContext, IMyPortfolioTableContext } from './MyPortfolioTableContext';
import { useAsyncData } from '@shared/react';
import { usePagination2 } from '@features/Pagination/usePagination';
import HandleSecurityRefreshStore from '@widgets/BrokerLevelPositionsTabs/store/handle-refresh-security-store';
import { getLogo } from '@widgets/TransactionsTable/api';
import { getMyPortfolioAsync } from '@widgets/MyPortfolioTable/api';
import { useSessionStore } from '@pages/AccountDetails/store/SessionIdStore';
import { fetchStockQuotes } from '@pages/AccountDetails/api/rewrites';

export interface ITransactionDesc {
    id: boolean;
    accountId: boolean;
    orderId: boolean;
    date: boolean;
    price: boolean;
    settlementDate: boolean;
    quantity: boolean;
    type: boolean;
    execId: boolean;
    processed: boolean;
    [key: string]: boolean;
}

export const MyPortfolioTableProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [accountId, setAccountId] = useState<any>(null);
    const [dateRange, setDateRange] = useState<[string | null, string | null]>(['', '']);
    const [sortFieldName, setSortFieldName] = useState('id');
    const refreshSecurity = HandleSecurityRefreshStore();
    const { sessionId, initializeSession, refreshSession } = useSessionStore();

    useEffect(() => {
        if (!sessionId) {
            initializeSession(); // Ensure session is initialized
        }
    }, [sessionId, initializeSession]);

    const [desc, setDesc] = useState<ITransactionDesc>({
        id: true,
        accountId: true,
        orderId: true,
        date: true,
        price: true,
        settlementDate: true,
        quantity: true,
        type: true,
        execId: true,
        processed: true,
    });

    const { page, pageSize, setTotalCount, paginationOptions } = usePagination2();

    const userStorage = localStorage.getItem('User');
    let userObject: any = null;
    let query: any = null;
    userObject = JSON.parse(userStorage ? userStorage : '');
    if (userObject?.accounts) {
        if (userObject.userRole === 5) {
            query = {
                page,
                pageSize,
                sortBy: `${sortFieldName} ${desc[sortFieldName] ? 'desc' : ''}`,
                filter: `AccountId=${userObject.accounts[0].id}`,
            };
        }
    }

    const {
        data: AssignPositionBody,
        handleRefresh: securityRefresh,
        loading: portfolioLoading,
    } = useAsyncData(
        async () => {
            if (!sessionId) return [];

            if (accountId !== null) {
                if (accountId !== 0) {
                    query.filter = `AccountId=${accountId}`;
                }
                const filterString = [query.filter].filter(Boolean).join(' , ');

                query.filter = filterString;

                const { items, totalResults } = await getMyPortfolioAsync(query);

                const symbols = items?.map((stock: any) => stock.symbol).join(',');

                const result = await fetchStockQuotes(symbols ? symbols : '', sessionId ? sessionId : '');

                if (result === null) {
                    await refreshSession();
                    return;
                }

                type StockInfo = {
                    lastPrice: number;
                    longname?: string;
                };

                const stockMap = new Map<string, StockInfo>(
                    result.quotedata.map((stock: any) => [
                        stock.symbol,
                        {
                            lastPrice: stock.pricedata?.last ?? 0,
                            longname: stock.longname,
                        },
                    ]),
                );

                setTotalCount(totalResults);

                const updatedData: any[] = (items ?? []).map((stock: any) => {
                    const stockInfo = stockMap.get(stock.symbol);
                    const latestPrice = stockInfo?.lastPrice ?? stock.buyingPrice;
                    const totalValue = latestPrice * stock.quantity;
                    const investmentReturn = totalValue - stock.amountInvested;

                    return {
                        name: stockInfo?.longname ?? stock.description,
                        symbol: stock.symbol,
                        icon: '',
                        amountInvested: `$${stock.amountInvested.toFixed(2)}`,
                        return: `$${investmentReturn.toFixed(2)}`,
                        isPositive: investmentReturn >= 0,
                        percentage: `${stock.investmentPercentage.toFixed(2)}`,
                    };
                });
                return updatedData || [];
            } else {
                return [];
            }
        },
        [],
        [pageSize, page, accountId, sessionId],
    );

    const { data: arrayWithLogos, loading: logoLoading } = useAsyncData(
        async () => {
            if (!AssignPositionBody) return [];
            return await Promise.all(
                AssignPositionBody.map(async (item: any) => {
                    const res = await getLogo({ symbol: item.symbol });
                    return { link: res, symbol: item.symbol };
                }),
            );
        },
        [],
        [AssignPositionBody],
    );

    useEffect(() => {
        if (refreshSecurity.refreshSecurity) {
            securityRefresh();
        }
    }, [refreshSecurity]);

    const value: IMyPortfolioTableContext = {
        dateRange,
        AssignPositionBody: AssignPositionBody ?? [],
        paginationOptions,
        portfolioLoading,
        arrayWithLogos,
        sortFieldName,
        desc,
        setAccountId,
    };

    return <MyPortfolioTableContext.Provider value={value}>{children}</MyPortfolioTableContext.Provider>;
};
